import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';

import FormField from './FormField';
import FormLabel from './FormLabel';
import FormMessage from './FormMessage';
import Responsive from './Responsive';

import utilStyles from '../styles/utils.module.scss';

const propTypes = {
  /** An `id` to used to associate the label and input. */
  controlId: PropTypes.string,

  /**
   * A `react-intl` message, used as the input label. The `string` variant is deprecated.
   *
   * **Note: labels should always be provided for a11y.
   * For cases where designs call for non-visible labels use `labelSrOnly`
   * in addition to a label.**
   */
  label: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),

  /** Prevent form errors from rendering. The `invalid` styles will still be applied. */
  hideError: PropTypes.bool,
  name: PropTypes.string,
  className: PropTypes.string,
};

function FieldGroup({
  controlId,
  label,
  name,
  hideError,
  withTooltip,
  alwaysShowLabel,
  className,
  intl,
  inputRef,
  ...props
}) {
  const id = controlId || `${name}Field`;
  const msgId = `${id}-form-error`;
  const showErrorInline = !hideError && !withTooltip;
  return (
    <Responsive smDown>
      {isSmall => (
        <div className={className}>
          {label && (
            <FormLabel
              htmlFor={id}
              className={isSmall && !alwaysShowLabel ? utilStyles.srOnly : ''}
            >
              {React.isValidElement(label) || typeof label === 'string' ? (
                label
              ) : (
                <FormattedMessage {...label} />
              )}
            </FormLabel>
          )}
          <FormField
            name={name}
            id={id}
            aria-describedby={showErrorInline ? msgId : undefined}
            innerRef={inputRef}
            withTooltip={withTooltip}
            {...props}
            placeholder={
              isSmall &&
              label &&
              !alwaysShowLabel &&
              !React.isValidElement(label)
                ? intl.formatMessage(label)
                : props.placeholder
            }
          />

          {showErrorInline && (
            <FormMessage for={name} id={msgId} aria-live="assertive" />
          )}
        </div>
      )}
    </Responsive>
  );
}

FieldGroup.propTypes = propTypes;

export default injectIntl(FieldGroup);
